import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ExternalLink, Eye, ShieldCheck, Users, ClipboardCheck, Megaphone, SearchCheck } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { api, fileUrl } from '../lib/api';
import { PageHeader, SectionHeader } from '../components/Shared';
import { PositionBox } from '../components/FactCheck';

const STEPS = [
  { icon: Megaphone, word: 'DECLARE IT.', body: 'Voters who choose to take part publicly declare who they voted for, on the record, by their own free choice.' },
  { icon: Eye, word: 'WITNESS IT.', body: 'Those voluntary declarations are witnessed and recorded openly, creating a public, people-powered picture.' },
  { icon: SearchCheck, word: 'VERIFY IT.', body: 'Anyone can compare that open, witnessed picture against announced results and ask questions when things do not line up.' },
];

const WHAT_IT_DOES = [
  { icon: Eye, title: 'Transparency people can see', body: 'A public window into how local election results are witnessed and reported, in plain language.' },
  { icon: Users, title: 'Made for regular people', body: 'No technical background needed. If you can read a notice board, you can follow Witness Your Vote.' },
  { icon: ClipboardCheck, title: 'Independent of any campaign', body: 'Witness Your Vote is its own initiative with its own website. It is not part of the campaign site or its data.' },
];

export default function WitnessYourVote() {
  const [assets, setAssets] = useState({ logo_url: '', logo_alt: '', video_url: '', video_embed_url: '', site_url: 'https://witnessyourvote.ca' });

  useEffect(() => {
    api.get('/wyv-assets').then((r) => setAssets((a) => ({ ...a, ...r.data }))).catch(() => {});
  }, []);

  const siteUrl = assets.site_url || 'https://witnessyourvote.ca';

  return (
    <div>
      <PageHeader
        pageKey="witness-your-vote"
        kicker="WITNESS YOUR VOTE"
        title="If we don't get paper ballots, the people still need transparency."
        subtitle="Introducing WitnessYourVote.ca, an independent civic transparency initiative developed by Steve because of concerns around election accountability."
      />

      {/* First choice: crystal clear */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14" data-testid="wyv-relationship-section">
        <div className="bg-white rounded-2xl border-l-4 border-[#C9A84C] border border-slate-200 p-6 sm:p-8" data-testid="wyv-first-choice-note">
          <div className="flex items-start gap-3">
            <ShieldCheck className="h-6 w-6 text-[#C9A84C] shrink-0 mt-0.5" />
            <div>
              <p className="font-serif font-black text-[#1a2744] text-xl sm:text-2xl leading-snug">MY FIRST CHOICE IS PAPER BALLOTS.</p>
              <p className="text-slate-700 mt-2 leading-relaxed">Witness Your Vote is not presented as a replacement for the official election. It is an independent civic transparency initiative Steve developed because of concerns around election accountability.</p>
              <p className="text-slate-700 mt-2 leading-relaxed">And here is the honest part: if we restore physical, independently recountable paper ballots, much of the reason for developing Witness Your Vote is reduced. That is the outcome Steve is fighting for first.</p>
              <Link to="/paper-ballots" className="inline-flex items-center gap-1 mt-3 text-sm font-bold text-[#1a2744] underline decoration-[#C9A84C] decoration-2 underline-offset-4" data-testid="wyv-paper-ballots-link">Read about the paper ballot campaign <ArrowRight className="h-3.5 w-3.5" /></Link>
            </div>
          </div>
        </div>

        {/* DECLARE IT. WITNESS IT. VERIFY IT. */}
        <div className="mt-8" data-testid="wyv-steps-section">
          <SectionHeader kicker="THE BASIC CONCEPT" title="Three simple steps" dek="The whole idea, in the plainest possible language." />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {STEPS.map(({ icon: Icon, word, body }, i) => (
              <Card key={word} className="card-lift" data-testid={`wyv-step-${i + 1}`}>
                <CardContent className="p-5">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="h-10 w-10 rounded-xl bg-[#1a2744] flex items-center justify-center shrink-0">
                      <Icon className="h-5 w-5 text-[#C9A84C]" />
                    </div>
                    <p className="font-serif font-black text-[#1a2744] text-lg">{word}</p>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed">{body}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <p className="text-xs text-slate-500 mt-4 leading-relaxed max-w-3xl" data-testid="wyv-voluntary-note">Participation is always voluntary. The official election and the secrecy of every ballot remain untouched. Witness Your Vote only records what participating voters freely choose to declare.</p>
        </div>

        {/* Official logo + welcome video surfaces.
            The video surface is HIDDEN entirely until a real video is entered
            through Admin (Steve's rule: never show an unfinished video
            placeholder on production). */}
        <div className={`grid grid-cols-1 ${(assets.video_embed_url || assets.video_url) ? 'lg:grid-cols-12' : ''} gap-6 mt-8`}>
          <div className={(assets.video_embed_url || assets.video_url) ? 'lg:col-span-4' : 'max-w-md'}>
            <Card data-testid="wyv-logo-card">
              <CardContent className="p-6 flex flex-col items-center text-center">
                {assets.logo_url ? (
                  <img src={fileUrl(assets.logo_url)} alt={assets.logo_alt || 'Witness Your Vote official logo'} className="h-32 w-auto max-w-full rounded-2xl" data-testid="wyv-official-logo" />
                ) : (
                  <>
                    <div className="h-32 w-32 rounded-3xl bg-[#1a2744] flex flex-col items-center justify-center shadow-lg" data-testid="wyv-placeholder-logo">
                      <Eye className="h-10 w-10 text-[#C9A84C]" />
                      <p className="text-white font-black text-xl mt-1 tracking-wide">WYV</p>
                    </div>
                    <p className="text-xs text-slate-500 mt-3" data-testid="wyv-logo-note">Temporary mark. The official Witness Your Vote logo will appear here.</p>
                  </>
                )}
                <Button asChild className="mt-4 h-11 w-full bg-[#C9A84C] hover:bg-[#b8973f] text-[#1a2744] font-bold btn-press" data-testid="wyv-visit-site-button">
                  <a href={siteUrl} target="_blank" rel="noopener noreferrer">Visit WitnessYourVote.ca <ExternalLink className="h-4 w-4 ml-1.5" /></a>
                </Button>
              </CardContent>
            </Card>
          </div>
          {(assets.video_embed_url || assets.video_url) && (
          <div className="lg:col-span-8">
            <Card className="overflow-hidden" data-testid="wyv-video-card">
              <CardContent className="p-0">
                {assets.video_embed_url ? (
                  <div className="aspect-video" data-testid="wyv-video-embed">
                    <iframe src={assets.video_embed_url} title="Witness Your Vote welcome video" className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                  </div>
                ) : (
                  <video src={fileUrl(assets.video_url)} controls className="w-full aspect-video bg-black" data-testid="wyv-video-player" />
                )}
              </CardContent>
            </Card>
          </div>
          )}
        </div>
      </section>

      {/* What it is */}
      <section className="bg-white border-y border-slate-200 py-10 md:py-14" data-testid="wyv-what-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader kicker="THE IDEA" title="What is Witness Your Vote?" dek="A simple promise: elections the people can watch, understand, and trust." />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {WHAT_IT_DOES.map(({ icon: Icon, title, body }) => (
              <Card key={title} className="card-lift" data-testid="wyv-what-card">
                <CardContent className="p-5">
                  <div className="h-10 w-10 rounded-xl bg-[#1a2744] flex items-center justify-center mb-3">
                    <Icon className="h-5 w-5 text-[#C9A84C]" />
                  </div>
                  <p className="font-serif font-bold text-[#1a2744] leading-snug">{title}</p>
                  <p className="text-sm text-slate-600 mt-2 leading-relaxed">{body}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <p className="text-xs text-slate-500 mt-6 leading-relaxed max-w-3xl" data-testid="wyv-independence-note">
            Witness Your Vote is a separate, independent platform with its own website and its own information. This page introduces the initiative; full details live at WitnessYourVote.ca as the project develops.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
        <PositionBox title="Accountability you can see." testId="wyv-cta-band">
          <p>First choice: restore the paper ballot option. Either way: transparency for the people.</p>
          <div className="mt-5 flex flex-col sm:flex-row gap-3">
            <Button asChild className="h-12 bg-[#C9A84C] hover:bg-[#b8973f] text-[#1a2744] font-black btn-press" data-testid="wyv-cta-visit">
              <a href={siteUrl} target="_blank" rel="noopener noreferrer">Visit WitnessYourVote.ca <ExternalLink className="h-4 w-4 ml-1.5" /></a>
            </Button>
            <Button asChild variant="outline" className="h-12 border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white font-bold btn-press" data-testid="wyv-cta-paper-ballots"><Link to="/paper-ballots">Why Paper Ballots? <ArrowRight className="h-4 w-4 ml-1" /></Link></Button>
          </div>
        </PositionBox>
      </section>
    </div>
  );
}
